import { useMemo, useState } from "react";
import {
  BookOpen,
  ClipboardList,
  FileText,
  Gauge,
  Lightbulb,
  Quote,
  Save,
  Settings,
  ToggleLeft,
  Trash2,
  Utensils,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import { Navigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { useLang } from "../../context/LangContext";
import { useV2Session } from "../context/V2SessionContext";
import { getV2Copy } from "../content";
import { V2_KNOWLEDGE_ITEM_TYPES } from "../lib/v2Schema";
import {
  createV2AdminMainFood,
  createV2ApprovedTip,
  createV2KnowledgeItem,
  createV2Quote,
  deleteV2ApprovedTip,
  deleteV2KnowledgeItem,
  deleteV2Quote,
  loadV2AdminAppSettings,
  loadV2AdminAuditLogs,
  loadV2AdminMainFoods,
  loadV2ApprovedTips,
  loadV2CustomFoods,
  loadV2KnowledgeItems,
  loadV2Quotes,
  saveV2AdminAppSettings,
  setV2AdminMainFoodActive,
  updateV2ApprovedTip,
  updateV2KnowledgeItem,
  updateV2Quote,
} from "../lib/v2Storage";

const tabs = [
  { key: "overview", label: "Overview", icon: Gauge },
  { key: "users", label: "Users", icon: Users },
  { key: "foods", label: "Foods", icon: Utensils },
  { key: "tips", label: "Tips", icon: Lightbulb },
  { key: "quotes", label: "Quotes", icon: Quote },
  { key: "knowledge", label: "Knowledge Base", icon: BookOpen },
  { key: "settings", label: "App Settings", icon: Settings },
  { key: "audit", label: "Audit Log", icon: ClipboardList },
];

const initialFoodForm = {
  name_en: "",
  name_ar: "",
  category: "",
  serving_quantity: "",
  serving_unit: "g",
  potassium_mg: "",
  phosphorus_mg: "",
  sodium_mg: "",
  protein_g: "",
  source: "Admin local review",
  verified: true,
  active: true,
  icon_key: "food",
};

const initialTipForm = {
  title_en: "",
  title_ar: "",
  body_en: "",
  body_ar: "",
  category: "",
  active: true,
  reviewed: true,
};

const initialQuoteForm = {
  quote_en: "",
  quote_ar: "",
  active: true,
  display_date: "",
};

const initialKnowledgeForm = {
  title: "",
  type: V2_KNOWLEDGE_ITEM_TYPES.manualNote,
  language: "en",
  category: "",
  active: true,
  reviewed: false,
  source_notes: "",
  content: "",
  file_name: "",
};

export default function V2Admin() {
  const { hasAdminAccess } = useV2Session();

  if (!hasAdminAccess) return <Navigate to="/dashboard" replace />;

  return <V2AdminContent />;
}

function V2AdminContent() {
  const { lang } = useLang();
  const copy = getV2Copy(lang);
  const { session, settings } = useV2Session();
  const [activeTab, setActiveTab] = useState("overview");
  const [mainFoods, setMainFoods] = useState(() => loadV2AdminMainFoods());
  const [customFoods, setCustomFoods] = useState(() => loadV2CustomFoods({ isGuest: false }));
  const [tips, setTips] = useState(() => loadV2ApprovedTips({ includeInactive: true }));
  const [quotes, setQuotes] = useState(() => loadV2Quotes({ includeInactive: true }));
  const [knowledgeItems, setKnowledgeItems] = useState(() => loadV2KnowledgeItems());
  const [adminSettings, setAdminSettings] = useState(() => loadV2AdminAppSettings());
  const [auditLogs, setAuditLogs] = useState(() => loadV2AdminAuditLogs());
  const [foodForm, setFoodForm] = useState(initialFoodForm);
  const [tipForm, setTipForm] = useState(initialTipForm);
  const [quoteForm, setQuoteForm] = useState(initialQuoteForm);
  const [knowledgeForm, setKnowledgeForm] = useState(initialKnowledgeForm);
  const [errors, setErrors] = useState({});

  const overviewStats = useMemo(
    () => [
      { label: "Main foods", value: mainFoods.length },
      { label: "Custom foods", value: customFoods.length },
      { label: "Approved tips", value: tips.filter((tip) => tip.active && tip.reviewed).length },
      { label: "Active quotes", value: quotes.filter((quote) => quote.active).length },
      { label: "Knowledge items", value: knowledgeItems.length },
      { label: "Audit entries", value: auditLogs.length },
    ],
    [auditLogs.length, customFoods.length, knowledgeItems.length, mainFoods.length, quotes, tips]
  );

  const refreshAdminData = () => {
    setMainFoods(loadV2AdminMainFoods());
    setCustomFoods(loadV2CustomFoods({ isGuest: false }));
    setTips(loadV2ApprovedTips({ includeInactive: true }));
    setQuotes(loadV2Quotes({ includeInactive: true }));
    setKnowledgeItems(loadV2KnowledgeItems());
    setAdminSettings(loadV2AdminAppSettings());
    setAuditLogs(loadV2AdminAuditLogs());
  };

  const submitFood = (event) => {
    event.preventDefault();
    const result = createV2AdminMainFood(foodForm);
    if (!result.ok) {
      setErrors(result.errors);
      toast.error("Check the food details.");
      return;
    }

    setFoodForm(initialFoodForm);
    setErrors({});
    refreshAdminData();
    toast.success("Food added.");
  };

  const submitTip = (event) => {
    event.preventDefault();
    const result = createV2ApprovedTip(tipForm);
    if (!result.ok) {
      setErrors(result.errors);
      toast.error("Check the tip details.");
      return;
    }

    setTipForm(initialTipForm);
    setErrors({});
    refreshAdminData();
    toast.success("Tip added.");
  };

  const submitQuote = (event) => {
    event.preventDefault();
    const result = createV2Quote(quoteForm);
    if (!result.ok) {
      setErrors(result.errors);
      toast.error("Check the quote details.");
      return;
    }

    setQuoteForm(initialQuoteForm);
    setErrors({});
    refreshAdminData();
    toast.success("Quote added.");
  };

  const submitKnowledgeItem = (event) => {
    event.preventDefault();
    const result = createV2KnowledgeItem(knowledgeForm);
    if (!result.ok) {
      setErrors(result.errors);
      toast.error("Check the knowledge item details.");
      return;
    }

    setKnowledgeForm(initialKnowledgeForm);
    setErrors({});
    refreshAdminData();
    toast.success("Knowledge item added.");
  };

  const saveAdminSettings = (event) => {
    event.preventDefault();
    saveV2AdminAppSettings(adminSettings);
    refreshAdminData();
    toast.success("App settings updated.");
  };

  return (
    <section className="mx-auto max-w-7xl space-y-6">
      <header className="grid gap-4 lg:grid-cols-[1fr_auto] lg:items-end">
        <div className="space-y-2">
          <p className="text-sm font-semibold text-primary">{copy.brand}</p>
          <h1 className="text-3xl font-extrabold tracking-tight md:text-4xl">Admin Dashboard</h1>
          <p className="max-w-3xl text-muted-foreground">
            Manage local food data, dashboard content, knowledge items, app settings, and audit entries.
          </p>
        </div>
        <div className="rounded-lg border border-border bg-card px-4 py-3 text-sm">
          <span className="font-semibold">Local controls only</span>
          <p className="mt-1 text-muted-foreground">Current session access is not production access control.</p>
        </div>
      </header>

      <nav className="flex gap-2 overflow-x-auto rounded-lg border border-border bg-card p-2">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <Button
              key={tab.key}
              type="button"
              variant={activeTab === tab.key ? "default" : "ghost"}
              className="shrink-0"
              onClick={() => setActiveTab(tab.key)}
            >
              <Icon className="h-4 w-4" />
              {tab.label}
            </Button>
          );
        })}
      </nav>

      {activeTab === "overview" && <OverviewSection stats={overviewStats} />}
      {activeTab === "users" && <UsersSection session={session} settings={settings} />}
      {activeTab === "foods" && (
        <FoodsSection
          mainFoods={mainFoods}
          customFoods={customFoods}
          form={foodForm}
          errors={errors}
          onSubmit={submitFood}
          onChange={(field, value) => setFoodForm((current) => ({ ...current, [field]: value }))}
          onToggle={(food) => {
            setV2AdminMainFoodActive(food.id, !food.active);
            refreshAdminData();
          }}
        />
      )}
      {activeTab === "tips" && (
        <TipsSection
          tips={tips}
          form={tipForm}
          errors={errors}
          onSubmit={submitTip}
          onChange={(field, value) => setTipForm((current) => ({ ...current, [field]: value }))}
          onToggle={(tip) => {
            updateV2ApprovedTip(tip.id, { ...tip, active: !tip.active });
            refreshAdminData();
          }}
          onDelete={(tip) => {
            if (!window.confirm(`Delete ${tip.title_en}?`)) return;
            deleteV2ApprovedTip(tip.id);
            refreshAdminData();
          }}
        />
      )}
      {activeTab === "quotes" && (
        <QuotesSection
          quotes={quotes}
          form={quoteForm}
          errors={errors}
          onSubmit={submitQuote}
          onChange={(field, value) => setQuoteForm((current) => ({ ...current, [field]: value }))}
          onToggle={(quote) => {
            updateV2Quote(quote.id, { ...quote, active: !quote.active });
            refreshAdminData();
          }}
          onDelete={(quote) => {
            if (!window.confirm("Delete this quote?")) return;
            deleteV2Quote(quote.id);
            refreshAdminData();
          }}
        />
      )}
      {activeTab === "knowledge" && (
        <KnowledgeSection
          items={knowledgeItems}
          form={knowledgeForm}
          errors={errors}
          onSubmit={submitKnowledgeItem}
          onChange={(field, value) => setKnowledgeForm((current) => ({ ...current, [field]: value }))}
          onToggle={(item) => {
            updateV2KnowledgeItem(item.id, { ...item, active: !item.active });
            refreshAdminData();
          }}
          onDelete={(item) => {
            if (!window.confirm(`Delete ${item.title}?`)) return;
            deleteV2KnowledgeItem(item.id);
            refreshAdminData();
          }}
        />
      )}
      {activeTab === "settings" && (
        <AppSettingsSection
          settings={adminSettings}
          onChange={(field, value) => setAdminSettings((current) => ({ ...current, [field]: value }))}
          onSubmit={saveAdminSettings}
        />
      )}
      {activeTab === "audit" && <AuditLogSection logs={auditLogs} />}
    </section>
  );
}

function OverviewSection({ stats }) {
  return (
    <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
      {stats.map((stat) => (
        <article key={stat.label} className="rounded-lg border border-border bg-card p-5">
          <p className="text-sm font-semibold text-muted-foreground">{stat.label}</p>
          <p className="mt-2 text-3xl font-extrabold">{stat.value}</p>
        </article>
      ))}
    </section>
  );
}

function UsersSection({ session, settings }) {
  return (
    <section className="rounded-lg border border-border bg-card p-5">
      <SectionTitle icon={Users} title="Current Local User" />
      <div className="mt-5 grid gap-3 md:grid-cols-2">
        <Metric label="Mode" value={session?.mode || "registered"} />
        <Metric label="Full name" value={settings.profile?.full_name || session?.user?.full_name || "Not set"} />
        <Metric label="Weight" value={settings.profile?.weight_kg ? `${settings.profile.weight_kg} kg` : "Not set"} />
        <Metric label="Language" value={settings.profile?.language || "en"} />
        <Metric label="Reminder channel" value={settings.reminders?.channel || "app"} />
        <Metric label="Notifications" value={settings.reminders?.notifications_enabled ? "Enabled" : "Disabled"} />
      </div>
    </section>
  );
}

function FoodsSection({ mainFoods, customFoods, form, errors, onSubmit, onChange, onToggle }) {
  return (
    <section className="grid gap-5 xl:grid-cols-[0.9fr_1.1fr]">
      <form className="rounded-lg border border-border bg-card p-5" onSubmit={onSubmit}>
        <SectionTitle icon={Utensils} title="Add Main Food" />
        <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm font-medium text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-100">
          Food values must be reviewed before production use.
        </p>
        <div className="mt-5 grid gap-4 md:grid-cols-2">
          <Field label="Food name English" htmlFor="food-name-en" error={errors.name_en}>
            <Input id="food-name-en" value={form.name_en} onChange={(event) => onChange("name_en", event.target.value)} />
          </Field>
          <Field label="Food name Arabic" htmlFor="food-name-ar">
            <Input id="food-name-ar" value={form.name_ar} onChange={(event) => onChange("name_ar", event.target.value)} />
          </Field>
          <Field label="Category" htmlFor="food-category" error={errors.category}>
            <Input id="food-category" value={form.category} onChange={(event) => onChange("category", event.target.value)} />
          </Field>
          <Field label="Serving unit" htmlFor="food-unit" error={errors.serving_unit}>
            <Input id="food-unit" value={form.serving_unit} onChange={(event) => onChange("serving_unit", event.target.value)} />
          </Field>
          <Field label="Serving quantity" htmlFor="food-serving" error={errors.serving_quantity}>
            <NumberInput id="food-serving" value={form.serving_quantity} onChange={(value) => onChange("serving_quantity", value)} />
          </Field>
          <Field label="Icon key" htmlFor="food-icon">
            <IconSelect id="food-icon" value={form.icon_key} onChange={(value) => onChange("icon_key", value)} />
          </Field>
          <Field label="Potassium" htmlFor="food-potassium" error={errors.potassium_mg}>
            <NumberInput id="food-potassium" value={form.potassium_mg} onChange={(value) => onChange("potassium_mg", value)} />
          </Field>
          <Field label="Phosphorus" htmlFor="food-phosphorus" error={errors.phosphorus_mg}>
            <NumberInput id="food-phosphorus" value={form.phosphorus_mg} onChange={(value) => onChange("phosphorus_mg", value)} />
          </Field>
          <Field label="Sodium" htmlFor="food-sodium" error={errors.sodium_mg}>
            <NumberInput id="food-sodium" value={form.sodium_mg} onChange={(value) => onChange("sodium_mg", value)} />
          </Field>
          <Field label="Protein" htmlFor="food-protein" error={errors.protein_g}>
            <NumberInput id="food-protein" value={form.protein_g} onChange={(value) => onChange("protein_g", value)} />
          </Field>
          <Field label="Source" htmlFor="food-source" error={errors.source}>
            <Input id="food-source" value={form.source} onChange={(event) => onChange("source", event.target.value)} />
          </Field>
          <ToggleRow label="Verified" checked={form.verified} onChange={(checked) => onChange("verified", checked)} />
          <ToggleRow label="Active" checked={form.active} onChange={(checked) => onChange("active", checked)} />
        </div>
        <div className="mt-5 flex justify-end">
          <Button type="submit">
            <Save className="h-4 w-4" />
            Add Main Food
          </Button>
        </div>
      </form>

      <div className="space-y-5">
        <AdminList
          title="Main Foods"
          items={mainFoods}
          renderItem={(food) => (
            <FoodRow key={food.id} food={food} onToggle={() => onToggle(food)} />
          )}
        />
        <AdminList
          title="Custom Foods"
          items={customFoods}
          emptyText="No custom foods stored locally."
          renderItem={(food) => (
            <div key={food.id} className="rounded-lg border border-border bg-background p-3 text-sm">
              <div className="flex items-center justify-between gap-3">
                <span className="font-bold">{food.name}</span>
                <span className="text-muted-foreground">{food.category || "Custom"}</span>
              </div>
            </div>
          )}
        />
      </div>
    </section>
  );
}

function TipsSection({ tips, form, errors, onSubmit, onChange, onToggle, onDelete }) {
  return (
    <ContentCrudSection
      icon={Lightbulb}
      title="Approved Tips"
      form={form}
      errors={errors}
      onSubmit={onSubmit}
      onChange={onChange}
      fields={[
        ["title_en", "Title English"],
        ["title_ar", "Title Arabic"],
        ["category", "Category"],
        ["body_en", "Body English"],
        ["body_ar", "Body Arabic"],
      ]}
      toggles={["active", "reviewed"]}
      submitLabel="Add Tip"
      items={tips}
      renderItem={(tip) => (
        <ContentRow
          key={tip.id}
          title={tip.title_en}
          detail={tip.body_en}
          meta={`${tip.category} - ${tip.reviewed ? "Reviewed" : "Needs review"}`}
          active={tip.active}
          onToggle={() => onToggle(tip)}
          onDelete={() => onDelete(tip)}
        />
      )}
    />
  );
}

function QuotesSection({ quotes, form, errors, onSubmit, onChange, onToggle, onDelete }) {
  return (
    <ContentCrudSection
      icon={Quote}
      title="Quotes"
      form={form}
      errors={errors}
      onSubmit={onSubmit}
      onChange={onChange}
      fields={[
        ["quote_en", "Quote English"],
        ["quote_ar", "Quote Arabic"],
        ["display_date", "Display date"],
      ]}
      toggles={["active"]}
      submitLabel="Add Quote"
      items={quotes}
      renderItem={(quote) => (
        <ContentRow
          key={quote.id}
          title={quote.quote_en}
          detail={quote.quote_ar || "No Arabic quote entered."}
          meta={quote.display_date || "Rotating quote"}
          active={quote.active}
          onToggle={() => onToggle(quote)}
          onDelete={() => onDelete(quote)}
        />
      )}
    />
  );
}

function KnowledgeSection({ items, form, errors, onSubmit, onChange, onToggle, onDelete }) {
  const storesText = ["manual_note", "txt", "markdown"].includes(form.type);

  return (
    <section className="grid gap-5 xl:grid-cols-[0.9fr_1.1fr]">
      <form className="rounded-lg border border-border bg-card p-5" onSubmit={onSubmit}>
        <SectionTitle icon={BookOpen} title="Knowledge Base" />
        <p className="mt-2 rounded-lg border border-border bg-muted p-3 text-sm font-medium">
          Knowledge items are stored locally until cloud storage is connected.
        </p>
        <div className="mt-5 grid gap-4 md:grid-cols-2">
          <Field label="Title" htmlFor="knowledge-title" error={errors.title}>
            <Input id="knowledge-title" value={form.title} onChange={(event) => onChange("title", event.target.value)} />
          </Field>
          <Field label="Type" htmlFor="knowledge-type" error={errors.type}>
            <select id="knowledge-type" className="h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm outline-none focus-visible:ring-1 focus-visible:ring-ring" value={form.type} onChange={(event) => onChange("type", event.target.value)}>
              <option value="manual_note">Manual note</option>
              <option value="pdf">PDF</option>
              <option value="docx">DOCX</option>
              <option value="txt">TXT</option>
              <option value="markdown">Markdown</option>
            </select>
          </Field>
          <Field label="Language" htmlFor="knowledge-language" error={errors.language}>
            <select id="knowledge-language" className="h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm outline-none focus-visible:ring-1 focus-visible:ring-ring" value={form.language} onChange={(event) => onChange("language", event.target.value)}>
              <option value="en">English</option>
              <option value="ar">Arabic</option>
            </select>
          </Field>
          <Field label="Category" htmlFor="knowledge-category" error={errors.category}>
            <Input id="knowledge-category" value={form.category} onChange={(event) => onChange("category", event.target.value)} />
          </Field>
          <Field label="Source / notes" htmlFor="knowledge-source">
            <Input id="knowledge-source" value={form.source_notes} onChange={(event) => onChange("source_notes", event.target.value)} />
          </Field>
          {!storesText && (
            <Field label="File name metadata" htmlFor="knowledge-file" error={errors.file_name}>
              <Input id="knowledge-file" value={form.file_name} onChange={(event) => onChange("file_name", event.target.value)} />
            </Field>
          )}
          {storesText && (
            <Field label="Content" htmlFor="knowledge-content" error={errors.content}>
              <textarea id="knowledge-content" className="min-h-28 w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm outline-none focus-visible:ring-1 focus-visible:ring-ring" value={form.content} onChange={(event) => onChange("content", event.target.value)} />
            </Field>
          )}
          <ToggleRow label="Active" checked={form.active} onChange={(checked) => onChange("active", checked)} />
          <ToggleRow label="Reviewed" checked={form.reviewed} onChange={(checked) => onChange("reviewed", checked)} />
        </div>
        <div className="mt-5 flex justify-end">
          <Button type="submit">
            <Save className="h-4 w-4" />
            Add Knowledge Item
          </Button>
        </div>
      </form>
      <AdminList
        title="Knowledge Items"
        items={items}
        emptyText="No knowledge items stored locally."
        renderItem={(item) => (
          <ContentRow
            key={item.id}
            title={item.title}
            detail={item.file_name || item.content || item.source_notes}
            meta={`${item.type} - ${item.language} - ${item.reviewed ? "Reviewed" : "Needs review"}`}
            active={item.active}
            onToggle={() => onToggle(item)}
            onDelete={() => onDelete(item)}
          />
        )}
      />
    </section>
  );
}

function AppSettingsSection({ settings, onChange, onSubmit }) {
  return (
    <form className="rounded-lg border border-border bg-card p-5" onSubmit={onSubmit}>
      <SectionTitle icon={Settings} title="App Settings" />
      <div className="mt-5 grid gap-4 md:grid-cols-2">
        <ToggleRow
          label="Show optional weight/fluid card"
          checked={settings.show_weight_fluid_card}
          onChange={(checked) => onChange("show_weight_fluid_card", checked)}
        />
        <Field label="Default daily reminder time" htmlFor="admin-daily-time">
          <Input id="admin-daily-time" type="time" value={settings.daily_system_reminder_time} onChange={(event) => onChange("daily_system_reminder_time", event.target.value)} />
        </Field>
        <Field label="Dashboard tip source" htmlFor="admin-tip-source">
          <select id="admin-tip-source" className="h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm outline-none focus-visible:ring-1 focus-visible:ring-ring" value={settings.dashboard_tip_source} onChange={(event) => onChange("dashboard_tip_source", event.target.value)}>
            <option value="admin">Admin-managed tips</option>
            <option value="default">Default tips</option>
          </select>
        </Field>
        <Field label="Active languages" htmlFor="admin-languages">
          <Input id="admin-languages" value={settings.active_languages.join(", ")} onChange={(event) => onChange("active_languages", event.target.value.split(",").map((item) => item.trim()))} />
        </Field>
        <div className="md:col-span-2">
          <Field label="Guidance text" htmlFor="admin-guidance">
            <textarea id="admin-guidance" className="min-h-24 w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm outline-none focus-visible:ring-1 focus-visible:ring-ring" value={settings.guidance_text} onChange={(event) => onChange("guidance_text", event.target.value)} />
          </Field>
        </div>
        <ToggleRow
          label="WhatsApp provider configured"
          checked={settings.whatsapp_provider_configured}
          onChange={(checked) => onChange("whatsapp_provider_configured", checked)}
        />
        <ToggleRow
          label="Use generic WhatsApp message by default"
          checked={settings.whatsapp_generic_message_default}
          onChange={(checked) => onChange("whatsapp_generic_message_default", checked)}
        />
        <div className="md:col-span-2">
          <Field label="Default WhatsApp message" htmlFor="admin-whatsapp-message">
            <Input
              id="admin-whatsapp-message"
              value={settings.default_whatsapp_message}
              onChange={(event) => onChange("default_whatsapp_message", event.target.value)}
            />
          </Field>
        </div>
      </div>
      <div className="mt-5 flex justify-end">
        <Button type="submit">
          <Save className="h-4 w-4" />
          Save App Settings
        </Button>
      </div>
    </form>
  );
}

function AuditLogSection({ logs }) {
  return (
    <AdminList
      title="Audit Log"
      items={logs}
      emptyText="No admin actions recorded locally."
      renderItem={(log) => (
        <div key={log.id} className="rounded-lg border border-border bg-background p-3 text-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="font-bold">{log.action}</span>
            <span className="text-muted-foreground">{formatDateTime(log.created_at)}</span>
          </div>
          <p className="mt-1 text-muted-foreground">
            {log.target_type} {log.target_id ? `- ${log.target_id}` : ""}
          </p>
          {log.details && <p className="mt-2">{log.details}</p>}
        </div>
      )}
    />
  );
}

function ContentCrudSection({ icon, title, form, errors, onSubmit, onChange, fields, toggles, submitLabel, items, renderItem }) {
  return (
    <section className="grid gap-5 xl:grid-cols-[0.9fr_1.1fr]">
      <form className="rounded-lg border border-border bg-card p-5" onSubmit={onSubmit}>
        <SectionTitle icon={icon} title={`Add ${title}`} />
        <div className="mt-5 grid gap-4 md:grid-cols-2">
          {fields.map(([field, label]) => (
            <Field key={field} label={label} htmlFor={`${title}-${field}`} error={errors[field]}>
              {field.includes("body") || field.includes("quote") ? (
                <textarea className="min-h-24 w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm outline-none focus-visible:ring-1 focus-visible:ring-ring" id={`${title}-${field}`} value={form[field]} onChange={(event) => onChange(field, event.target.value)} />
              ) : (
                <Input id={`${title}-${field}`} type={field === "display_date" ? "date" : "text"} value={form[field]} onChange={(event) => onChange(field, event.target.value)} />
              )}
            </Field>
          ))}
          {toggles.map((field) => (
            <ToggleRow key={field} label={titleCase(field)} checked={form[field]} onChange={(checked) => onChange(field, checked)} />
          ))}
        </div>
        <div className="mt-5 flex justify-end">
          <Button type="submit">
            <Save className="h-4 w-4" />
            {submitLabel}
          </Button>
        </div>
      </form>
      <AdminList title={title} items={items} renderItem={renderItem} />
    </section>
  );
}

function AdminList({ title, items, renderItem, emptyText = "No records stored locally." }) {
  return (
    <section className="rounded-lg border border-border bg-card p-5">
      <SectionTitle icon={FileText} title={title} />
      {items.length > 0 ? (
        <div className="mt-4 space-y-3">{items.map(renderItem)}</div>
      ) : (
        <p className="mt-4 rounded-lg bg-muted p-4 text-sm text-muted-foreground">{emptyText}</p>
      )}
    </section>
  );
}

function FoodRow({ food, onToggle }) {
  return (
    <div className="rounded-lg border border-border bg-background p-3 text-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-bold">{food.name_en}</h3>
          <p className="text-muted-foreground">
            {food.category} - {food.serving_quantity} {food.serving_unit}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">{food.source}</p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={onToggle}>
          <ToggleLeft className="h-4 w-4" />
          {food.active ? "Deactivate" : "Activate"}
        </Button>
      </div>
    </div>
  );
}

function ContentRow({ title, detail, meta, active, onToggle, onDelete }) {
  return (
    <article className="rounded-lg border border-border bg-background p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-bold">{title}</h3>
            <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${active ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200" : "bg-slate-100 text-slate-700 dark:bg-slate-900 dark:text-slate-200"}`}>
              {active ? "Active" : "Inactive"}
            </span>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">{meta}</p>
          {detail && <p className="mt-2 text-sm">{detail}</p>}
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onToggle}>
            <ToggleLeft className="h-4 w-4" />
            Toggle
          </Button>
          <Button type="button" variant="ghost" size="sm" className="text-destructive" onClick={onDelete}>
            <Trash2 className="h-4 w-4" />
            Delete
          </Button>
        </div>
      </div>
    </article>
  );
}

function SectionTitle({ icon: Icon, title }) {
  return (
    <div className="flex items-center gap-3">
      <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground">
        <Icon className="h-4 w-4" />
      </span>
      <h2 className="text-lg font-bold">{title}</h2>
    </div>
  );
}

function Field({ label, htmlFor, error, children }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {error && <p className="text-sm font-medium text-destructive">{error}</p>}
    </div>
  );
}

function ToggleRow({ label, checked, onChange }) {
  return (
    <div className="flex min-h-12 items-center justify-between gap-4 rounded-lg border border-border bg-background px-3 py-2">
      <span className="text-sm font-medium">{label}</span>
      <Switch checked={Boolean(checked)} onCheckedChange={onChange} />
    </div>
  );
}

function NumberInput({ id, value, onChange }) {
  return (
    <Input id={id} type="number" min="0" step="0.01" value={value} onChange={(event) => onChange(event.target.value)} />
  );
}

function IconSelect({ id, value, onChange }) {
  return (
    <select id={id} className="h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm outline-none focus-visible:ring-1 focus-visible:ring-ring" value={value} onChange={(event) => onChange(event.target.value)}>
      <option value="food">Food</option>
      <option value="fruit">Fruit</option>
      <option value="vegetable">Vegetable</option>
      <option value="protein">Protein</option>
      <option value="grain">Grain</option>
      <option value="dairy">Dairy</option>
      <option value="meal">Meal</option>
    </select>
  );
}

function Metric({ label, value }) {
  return (
    <div className="rounded-lg border border-border bg-background p-4">
      <span className="block text-xs font-medium text-muted-foreground">{label}</span>
      <span className="mt-1 block font-bold">{value}</span>
    </div>
  );
}

function titleCase(value) {
  return String(value || "")
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function formatDateTime(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
